import { get } from '../client';
import type { HealthStatus, ReleaseFeature, ReleaseManifest, ReleasePhase, SystemBannerItem, SystemBannerKind } from '../types/platform';
import type { Page } from '../types/common';

type Raw = Record<string, unknown>;

const titleOf = (key: string) => key.replace(/[._]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/** core-api banners carry `severity` (info|warning|critical) rather than a UI kind (R-144). */
export function normalizeBanner(raw: unknown): SystemBannerItem {
  const r = (raw ?? {}) as Raw;
  const severity = String(r.severity ?? '');
  const message = String(r.message ?? '');
  const kind: SystemBannerKind =
    (typeof r.kind === 'string' ? (r.kind as SystemBannerKind) : undefined) ??
    (/downtime|read-only/i.test(message) ? 'downtime' : /\bAI\b|drafting/i.test(message) ? 'ai-off' : /support/i.test(message) ? 'support' : severity === 'info' ? 'test' : 'degraded');
  return {
    id: String(r.id ?? message),
    kind,
    message,
    meta: typeof r.meta === 'string' ? r.meta : undefined,
    startsAt: (r.startsAt as string | undefined) ?? undefined,
    endsAt: (r.endsAt as string | null | undefined) ?? undefined,
    readOnly: typeof r.readOnly === 'boolean' ? r.readOnly : kind === 'downtime',
  };
}

export function normalizeManifest(raw: unknown): ReleaseManifest {
  const r = (raw ?? {}) as Raw;
  const features = (Array.isArray(r.features) ? (r.features as Raw[]) : []).map<ReleaseFeature>((f) => ({
    key: String(f.key),
    name: typeof f.name === 'string' ? f.name : titleOf(String(f.key)),
    phase: (f.phase as ReleasePhase) ?? 'F1',
    enabled: !!f.enabled,
    reason: typeof f.reason === 'string' ? f.reason : f.enabled ? undefined : 'Not in this release',
    description: typeof f.description === 'string' ? f.description : undefined,
  }));
  return {
    version: String(r.version ?? r.release ?? ''),
    build: String(r.build ?? r.release ?? ''),
    tenantId: (r.tenantId as string | null) ?? null,
    generatedAt: String(r.generatedAt ?? new Date().toISOString()),
    features,
  };
}

const normalizeHealth = (raw: unknown): HealthStatus => {
  const r = (raw ?? {}) as Raw;
  const s = String(r.status ?? '');
  return { ...(r as object), status: s === 'ok' || s === 'ready' ? 'ok' : s === 'degraded' ? 'degraded' : 'down' } as HealthStatus;
};

export const platformApi = {
  health: async () => normalizeHealth(await get<unknown>('/health', undefined, { auth: false })),
  ready: async () => normalizeHealth(await get<unknown>('/ready', undefined, { auth: false })),
  releaseManifest: async () => normalizeManifest(await get<unknown>('/release-manifest')),
  systemBanners: async (): Promise<Page<SystemBannerItem>> => {
    const r = await get<{ items?: unknown[]; nextCursor?: string | null }>('/system-banners');
    return { items: (r.items ?? []).map(normalizeBanner), nextCursor: r.nextCursor ?? null };
  },
};
